// File: frontend/src/components/dfs/ScoreBreakdownModal.tsx
//
// Per-player score breakdown for one match: a pinned pitch replay of the
// scoring moments (MomentReplayPanel), one row per moment (tap to replay it),
// and the per-stat summary at the bottom. Rows always come from the `scores`
// the caller passes, so they match the points the user tapped; the moments
// endpoint adds the replay clips and the "created a shot" lines on top.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiFantasyScore, PlayerRead } from '@/api/schemas';
import { fetchPlayerMatchMoments } from '@/api/stats';
import SafeViewportOverlay from '@/components/common/SafeViewportOverlay';
import { useAuth } from '@/contexts/AuthContext';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useExitTransition } from '@/hooks/useExitTransition';
import { useAppBackButtonInterceptor } from '@/hooks/useAppBackButtonInterceptor';
import { trackPlayerResearch } from '@/utils/research';
import { formatGametime, formatPlayerDisplayName, formatStatName } from '@/utils/formatting';
import { getAbbreviation } from '@/utils/teams';
import { userFacingError } from '@/utils/userFacingError';
import { ApiError } from '@/utils/appError';
import {
  PlayerMatchMoments,
  buildMomentRows,
  buildReplayItems,
  carryEndTimes,
  prepareClip,
  PreparedClip,
  replayMoments,
  signedPoints,
} from '@/utils/momentReplay';
import { isCancel } from '@/api/client';
import { AlertTriangle, Play } from 'lucide-react';
import { getScoreTypeIcon, getScoreTypeColorClass } from './scoreTypeIcons';
import MomentReplayPanel, { MomentBadgeLabel, MomentReplayHandle } from './MomentReplayPanel';

interface ScoreBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  player: PlayerRead | null;
  scores: ApiFantasyScore[] | null;
  isLoading: boolean;
  error: string | null;
  matchId: number | null;
}

// Row chips follow the replay colors (and the Film Room scoring log): shots
// green, creation blue, Impact gold, lost ball red.
const CHIP_CLASSES: Record<string, string> = {
  Goal: 'bg-green-500/15 text-green-400',
  ShotOnTarget: 'bg-green-500/15 text-green-400',
  Shot: 'bg-green-500/15 text-green-400',
  GCA: 'bg-[#78b4e6]/15 text-[#78b4e6]',
  SCA: 'bg-[#78b4e6]/15 text-[#78b4e6]',
  ProgPass: 'bg-category-progressing/15 text-category-progressing',
  ProgCarry: 'bg-category-progressing/15 text-category-progressing',
  Dispossessed: 'bg-red-500/15 text-red-400',
};

type MomentsState =
  | { status: 'idle' | 'loading' | 'unavailable'; requestKey: string | null; data: null }
  | { status: 'ready'; requestKey: string; data: PlayerMatchMoments };

const ScoreBreakdownModal: React.FC<ScoreBreakdownModalProps> = ({
  isOpen,
  onClose,
  player,
  scores,
  isLoading,
  error,
  matchId,
}) => {
  const { token } = useAuth();
  const [moments, setMoments] = useState<MomentsState>({ status: 'idle', requestKey: null, data: null });
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const replayRef = useRef<MomentReplayHandle>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef(new Map<string, HTMLElement>());
  const rowRefCallbacks = useRef(new Map<string, (node: HTMLElement | null) => void>());
  const { render, closing } = useExitTransition(isOpen);
  useBodyScrollLock(render);
  useAppBackButtonInterceptor(render, onClose);
  useEffect(() => {
    if (!render) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [render, onClose]);

  // Conviction signal (STRATEGY C-ENG2): inspecting where the points came
  // from is the deepest scoring-engine research action.
  useEffect(() => {
    if (isOpen && player?.id) {
      trackPlayerResearch('score_breakdown', { 'Player ID': player.id });
    }
  }, [isOpen, player?.id]);

  const requestKey = player?.id && matchId ? `${player.id}:${matchId}` : null;

  // Replays load lazily with the modal. Any failure just means no pitch: the
  // rows and total still come from the caller's scores. Reopening the same
  // player keeps the loaded replay on screen while it refreshes (live matches
  // gain moments), and a failed refresh keeps it too.
  useEffect(() => {
    // Closing leaves the replay as it is through the exit animation; the
    // panel stops when it unmounts.
    if (!isOpen) return;
    setFocusKey(null);
    if (!requestKey || !player?.id || !matchId || !token) {
      setMoments({ status: 'unavailable', requestKey, data: null });
      return;
    }
    const controller = new AbortController();
    setMoments(current => (current.status === 'ready' && current.requestKey === requestKey
      ? current
      : { status: 'loading', requestKey, data: null }));
    fetchPlayerMatchMoments(player.id, matchId, token, controller.signal)
      .then(data => {
        if (!controller.signal.aborted) setMoments({ status: 'ready', requestKey, data });
      })
      .catch((fetchError: unknown) => {
        if (controller.signal.aborted || isCancel(fetchError)) return;
        // Rate limits, expired sessions and unknown players are not our errors.
        const status = fetchError instanceof ApiError ? fetchError.status : undefined;
        if (status === undefined || status >= 500) userFacingError(fetchError, null, { section: 'moment_replays' });
        setMoments(current => (current.status === 'ready' && current.requestKey === requestKey
          ? current
          : { status: 'unavailable', requestKey, data: null }));
      });
    return () => controller.abort();
  }, [isOpen, requestKey, player?.id, matchId, token]);

  const momentsData = moments.status === 'ready' && moments.requestKey === requestKey ? moments.data : null;
  // Until this player's request settles, hold the pitch's space (no flash of "no pitch").
  const momentsStatus: 'loading' | 'ready' | 'unavailable' = momentsData ? 'ready'
    : moments.status === 'unavailable' && moments.requestKey === requestKey ? 'unavailable'
    : requestKey && token ? 'loading' : 'unavailable';

  // Teammate names for the pitch labels and result lines, nickname first.
  const names = useMemo(() => {
    const display: Record<string, string> = {};
    Object.entries(momentsData?.names ?? {}).forEach(([id, person]) => {
      display[id] = formatPlayerDisplayName({ id: Number(id), nickname: person.nickname, name: person.name });
    });
    return display;
  }, [momentsData]);

  const rows = useMemo(() => buildMomentRows(scores ?? [], momentsData, names), [scores, momentsData, names]);

  const replay = useMemo(() => {
    if (!momentsData) return null;
    const clips = new Map<string, PreparedClip>();
    momentsData.clips.forEach(clip => {
      const prepared = prepareClip(clip, carryEndTimes(momentsData.moments, clip.id));
      if (prepared) clips.set(clip.id, prepared);
    });
    const replayable = replayMoments(momentsData, rows);
    const items = buildReplayItems(replayable, clips, momentsData.player.id);
    const keys = new Set(items.flatMap(item => item.pops.map(pop => pop.key)));
    return { moments: replayable, items, keys };
  }, [momentsData, rows]);

  const badgeLabels = useMemo(
    () => new Map<string, MomentBadgeLabel>(rows.map(row => [row.key, { minute: formatGametime(row.period, row.minute), points: row.points }])),
    [rows],
  );

  const summary = useMemo(() => {
    if (!scores || scores.length === 0) return null;

    const statCounts: Record<string, { count: number; totalPoints: number }> = {};
    let totalScore = 0;

    scores.forEach(score => {
      const type = score.score_type || 'Unknown';
      if (!statCounts[type]) {
        statCounts[type] = { count: 0, totalPoints: 0 };
      }
      statCounts[type].count += 1;
      statCounts[type].totalPoints += score.points || 0;
      totalScore += score.points || 0;
    });

    const sortedStats = Object.entries(statCounts)
      .sort(([, a], [, b]) => b.totalPoints - a.totalPoints)
      .map(([type, data]) => ({
        type,
        ...data,
        avgPoints: data.totalPoints / data.count
      }));

    return { sortedStats, totalScore };
  }, [scores]);

  // Keep the playing row in view below the pinned pitch.
  useEffect(() => {
    if (!focusKey) return;
    const body = bodyRef.current;
    const row = rowRefs.current.get(focusKey);
    if (!body || !row) return;
    const pinned = pinnedRef.current?.offsetHeight ?? 0;
    const top = row.offsetTop - pinned - 8;
    const bottom = row.offsetTop + row.offsetHeight - body.clientHeight + 8;
    if (body.scrollTop > top) body.scrollTo({ top, behavior: 'smooth' });
    else if (body.scrollTop < bottom) body.scrollTo({ top: bottom, behavior: 'smooth' });
  }, [focusKey]);

  // One stable ref callback per row, so re-renders during playback don't detach them.
  const setRowRef = useCallback((key: string) => {
    let callback = rowRefCallbacks.current.get(key);
    if (!callback) {
      callback = (node: HTMLElement | null) => {
        if (node) rowRefs.current.set(key, node);
        else rowRefs.current.delete(key);
      };
      rowRefCallbacks.current.set(key, callback);
    }
    return callback;
  }, []);

  if (!render) {
    return null;
  }

  const displayPlayerName = formatPlayerDisplayName(player).toUpperCase();
  const matchLabel = momentsData?.match.home_team && momentsData.match.away_team
    ? `${getAbbreviation(momentsData.match.home_team)} v ${getAbbreviation(momentsData.match.away_team)}`
    : null;
  let featuredTeam: string | null = null;
  if (momentsData && momentsData.match.team_id != null) {
    const { team_id, home_team_id, away_team_id, home_team, away_team } = momentsData.match;
    featuredTeam = team_id === home_team_id ? home_team : team_id === away_team_id ? away_team : null;
  }
  const hasRows = !!scores && scores.length > 0 && !isLoading && !error;

  return (
    <SafeViewportOverlay
      onClose={onClose}
      closing={closing}
      zIndexClassName="z-[70]"
      className="flex-col items-center"
    >
      <div
        className={`relative flex min-h-0 max-h-full w-full max-w-lg flex-1 flex-col overflow-hidden rounded-lg border border-gray-700/50 bg-surface-card shadow-xl sm:flex-none sm:max-h-[85dvh] ${closing ? 'animate-scale-out' : 'animate-scale-in'}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Score breakdown"
      >
        {/* Header */}
        <div className="bg-primary-dark px-4 py-3 border-b border-gray-700 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex-1 min-w-0 pr-4">
              <h2 className="font-heading font-bold text-lg text-accent-yellow truncate">
                Score Breakdown
              </h2>
              <p className="text-xs text-gray-400 mt-0.5 truncate">
                {displayPlayerName}{matchLabel ? ` · ${matchLabel}` : ''}
              </p>
            </div>
            <button
              onClick={onClose}
              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-2xl font-bold leading-none text-gray-400 transition-colors hover:bg-primary/50 hover:text-white active:bg-primary/50 active:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow"
              aria-label="Close score breakdown modal"
            >
              ×
            </button>
          </div>
        </div>

        <div ref={bodyRef} className="relative flex-1 overflow-y-auto overscroll-contain scrollbar-mobile-hide">
          {hasRows && summary && player && (
            <div ref={pinnedRef} className="sticky top-0 z-10">
              <MomentReplayPanel
                ref={replayRef}
                status={momentsStatus}
                items={replay?.items ?? []}
                moments={replay?.moments ?? []}
                playerId={player.id}
                featuredName={formatPlayerDisplayName(player)}
                names={names}
                directionLabel={featuredTeam ? `${getAbbreviation(featuredTeam)} attacking →` : null}
                labels={badgeLabels}
                total={summary.totalScore}
                onFocusChange={setFocusKey}
              />
            </div>
          )}

          {isLoading && (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-accent-yellow mb-2"></div>
                <p className="text-gray-400 text-sm">Loading score breakdown...</p>
              </div>
            </div>
          )}

          {error && !isLoading && (
            <div className="flex-1 flex items-center justify-center p-4">
              <div className="text-center">
                <AlertTriangle size={24} className="text-secondary mb-2 mx-auto" />
                <p className="text-secondary text-sm">{error}</p>
              </div>
            </div>
          )}

          {scores && !isLoading && !error && scores.length === 0 && (
            <div className="flex-1 flex items-center justify-center p-4">
              <p className="text-gray-500 italic text-sm">
                No scoring events recorded for this {matchId ? 'match' : 'contest'}.
              </p>
            </div>
          )}

          {hasRows && (
            <>
              <section className="px-3 pt-3 pb-1">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">Moments</h3>
                <div className="grid gap-1.5">
                  {rows.map(row => {
                    const replayable = !!replay?.keys.has(row.key);
                    const active = focusKey === row.key;
                    const body = (
                      <>
                        {replayable && <span className="sr-only">Replay this moment: </span>}
                        <span className="font-heading text-base font-bold leading-snug text-white">
                          {formatGametime(row.period, row.minute)}
                        </span>
                        <span className="grid min-w-0 gap-1">
                          <span className="flex flex-wrap gap-1">
                            {row.scores.map((score, i) => (
                              <span key={`${score.type}-${i}`} className={`inline-block rounded-md px-1.5 py-0.5 text-xs font-medium ${CHIP_CLASSES[score.type] ?? 'bg-gray-500/15 text-gray-300'}`}>
                                {formatStatName(score.type)}
                                {score.via && <span className="font-normal opacity-70"> ({score.via})</span>}
                                {' '}{signedPoints(score.points)}
                              </span>
                            ))}
                          </span>
                          {row.resultText && <span className="text-[12.5px] text-[#78b4e6]">↳ {row.resultText}</span>}
                        </span>
                        <span className={`flex items-center gap-1.5 font-heading text-base font-bold leading-snug tabular-nums ${getScoreTypeColorClass(row.points)}`}>
                          {signedPoints(row.points)}
                          {replayable && <Play size={10} fill="currentColor" className={active ? 'text-accent-yellow' : 'text-gray-500'} aria-hidden="true" />}
                        </span>
                      </>
                    );
                    const className = `grid w-full grid-cols-[2.75rem_minmax(0,1fr)_auto] items-start gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors ${active ? 'border-accent-yellow bg-surface-raised' : 'border-transparent bg-primary/40'}`;
                    return replayable ? (
                      <button
                        key={row.key}
                        ref={setRowRef(row.key)}
                        type="button"
                        onClick={() => replayRef.current?.playMoment(row.key)}
                        className={`${className} hover:bg-primary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow`}
                      >
                        {body}
                      </button>
                    ) : (
                      <div key={row.key} ref={setRowRef(row.key)} className={className}>
                        {body}
                      </div>
                    );
                  })}
                </div>
              </section>

              {summary && (
                <section className="px-4 pt-3 pb-4">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Summary</h3>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 font-medium">
                        <th className="text-left py-1 pr-2">Type</th>
                        <th className="text-center py-1 px-2">#</th>
                        <th className="text-center py-1 px-2">Pts</th>
                        <th className="text-right py-1 pl-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.sortedStats.map(({ type, count, totalPoints, avgPoints }) => (
                        <tr key={type} className="border-t border-gray-700/30">
                          <td className="py-1.5 pr-2">
                            <div className="flex items-center space-x-1.5">
                              <span className="text-sm">{getScoreTypeIcon(type)}</span>
                              <span className="font-semibold text-gray-300">{formatStatName(type)}</span>
                            </div>
                          </td>
                          <td className="py-1.5 px-2 text-center text-gray-300">{count}</td>
                          <td className={`py-1.5 px-2 text-center font-semibold ${getScoreTypeColorClass(avgPoints)}`}>
                            {avgPoints.toFixed(2)}
                          </td>
                          <td className={`py-1.5 pl-2 text-right font-bold ${getScoreTypeColorClass(totalPoints)}`}>
                            {totalPoints >= 0 ? '+' : ''}{totalPoints.toFixed(1)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}
            </>
          )}

          {!scores && !isLoading && !error && (
            <div className="flex-1 flex items-center justify-center p-4">
              <p className="text-gray-500 text-sm">Score breakdown unavailable.</p>
            </div>
          )}
        </div>
      </div>
    </SafeViewportOverlay>
  );
};

export default ScoreBreakdownModal;
